import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  MessageSquare,
  DollarSign,
  FileUp,
  Briefcase,
  Info,
  CheckCheck,
  CalendarPlus,
  CalendarX2,
  CalendarClock,
} from "lucide-react";
import { notificationsApi } from "../../services/api";

interface Notification {
  _id: string;
  type: string;
  title: string;
  body: string | null;
  link: {
    entity: string | null;
    entityId: string | null;
    url: string | null;
  } | null;
  read: boolean;
  createdAt: string;
}

const TYPE_ICONS: Record<string, { icon: React.FC<any>; color: string }> = {
  task_assigned: { icon: ClipboardCheck, color: "text-blue-500" },
  approval_needed: { icon: CheckCircle2, color: "text-amber-500" },
  deadline_approaching: { icon: Clock, color: "text-red-500" },
  message_received: { icon: MessageSquare, color: "text-purple-500" },
  payment_received: { icon: DollarSign, color: "text-emerald-500" },
  document_uploaded: { icon: FileUp, color: "text-indigo-500" },
  matter_updated: { icon: Briefcase, color: "text-blue-500" },
  system: { icon: Info, color: "text-slate-500" },
  booking_created: { icon: CalendarPlus, color: "text-emerald-500" },
  booking_cancelled: { icon: CalendarX2, color: "text-red-500" },
  booking_rescheduled: { icon: CalendarClock, color: "text-amber-500" },
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsApi.unreadCount();
      setUnreadCount(res.data?.count || 0);
    } catch {
      // Silently fail
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ limit: 10, sort: "createdAt", order: "desc" });
      setNotifications(res.data?.data || []);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for unread count
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // every 30 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Close on click outside (check both bell button and portal dropdown)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const handleNotificationClick = (n: Notification) => {
    if (!n.read) handleMarkRead(n._id);
    if (n.link?.url) {
      navigate(n.link.url);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-white/60 hover:text-white transition-colors"
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — rendered via portal to escape sidebar stacking context */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="fixed bottom-4 left-[272px] w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-[9999] overflow-hidden max-md:left-4 max-md:right-4 max-md:w-auto max-md:bottom-20"
            >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs font-medium flex items-center gap-1 hover:underline"
                    style={{ color: "var(--color-primary)" }}
                  >
                    <CheckCheck className="h-3 w-3" />
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                  <Bell className="h-8 w-8 mb-2 text-slate-300" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                notifications.map((n) => {
                  const typeInfo = TYPE_ICONS[n.type] || TYPE_ICONS.system;
                  const Icon = typeInfo.icon;
                  return (
                    <div
                      key={n._id}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                        !n.read ? "bg-brand-light/30" : ""
                      }`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <div className={`mt-0.5 ${typeInfo.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!n.read ? "font-semibold text-slate-800" : "text-slate-700"} truncate`}>
                            {n.title}
                          </p>
                          {!n.read && <div className="h-2 w-2 rounded-full bg-brand-accent flex-shrink-0 mt-1.5" />}
                        </div>
                        {n.body && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{timeAgo(n.createdAt)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-4 py-2">
              <button
                onClick={() => {
                  navigate("/notifications");
                  setOpen(false);
                }}
                className="w-full text-center text-sm font-medium py-1 rounded-lg hover:bg-slate-50 transition-colors"
                style={{ color: "var(--color-primary)" }}
              >
                View all notifications
              </button>
            </div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default NotificationDropdown;
